/**
 * Entfernungen und Gruppierung — reine Rechnung, ohne Karte.
 *
 * Die Umkreissuche beantwortet die Frage, die man sich auf einer neuen Reise
 * stellt: "Bin ich hier schon getaucht?" Gerechnet wird mit der Haversine-Formel
 * auf einer Kugel. Für Tauchplätze ist das genau genug — die Abweichung zum
 * Ellipsoid liegt bei einem halben Prozent, und niemand sucht Tauchplätze auf
 * hundert Meter genau.
 */

const ERDRADIUS_KM = 6371;
const grad = (wert) => (wert * Math.PI) / 180;

export function entfernung(punktA, punktB) {
  const dLat = grad(punktB.lat - punktA.lat);
  const dLon = grad(punktB.lon - punktA.lon);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(grad(punktA.lat)) * Math.cos(grad(punktB.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * ERDRADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function hatKoordinaten(platz) {
  return typeof platz?.lat === 'number' && typeof platz?.lon === 'number'
    && !(platz.lat === 0 && platz.lon === 0);   // 0/0 ist der Nullmeridian, kein Tauchplatz
}

/** Alle Plätze innerhalb des Umkreises, nächster zuerst. */
export function umkreis(plaetze, punkt, radiusKm) {
  return plaetze
    .filter(hatKoordinaten)
    .map((platz) => ({ platz, km: entfernung(punkt, platz) }))
    .filter((treffer) => treffer.km <= radiusKm)
    .sort((a, b) => a.km - b.km);
}

/**
 * Tauchplätze mit ihren Kennzahlen — ein Marker je Platz, nicht je Tauchgang.
 * Ohne das läge auf einem Hausriff mit 40 Tauchgängen ein Stapel aus 40 Nadeln.
 */
export function plaetzeMitTauchgaengen(tauchgaenge, plaetze) {
  const nachId = new Map();
  for (const platz of plaetze) {
    if (hatKoordinaten(platz)) nachId.set(platz.id, { ...platz, tauchgaenge: [] });
  }
  for (const tg of tauchgaenge) {
    nachId.get(tg.site_id)?.tauchgaenge.push(tg);
  }
  return [...nachId.values()]
    .filter((platz) => platz.tauchgaenge.length > 0)
    .map((platz) => {
      const tiefen = platz.tauchgaenge
        .map((tg) => tg.max_depth_m)
        .filter((wert) => typeof wert === 'number');
      const zeiten = platz.tauchgaenge.map((tg) => tg.start_utc).filter(Boolean).sort();
      return {
        ...platz,
        anzahl: platz.tauchgaenge.length,
        maxTiefe: tiefen.length ? Math.max(...tiefen) : null,
        schnittTiefe: tiefen.length ? tiefen.reduce((a, b) => a + b, 0) / tiefen.length : null,
        erster: zeiten[0] ?? null,
        letzter: zeiten[zeiten.length - 1] ?? null,
      };
    })
    .sort((a, b) => b.anzahl - a.anzahl);
}

/**
 * Gruppierung über ein Gitter.
 *
 * Auf Weltzoom liegen die Malediven-Plätze aufeinander. Statt eine
 * Cluster-Bibliothek zu laden, werden Plätze in Gitterzellen zusammengefasst,
 * deren Kantenlänge am Zoom hängt: je weiter weg, desto gröber.
 */
export function gruppiere(plaetze, zoom) {
  const kante = 360 / 2 ** (Number(zoom) + 3);   // Grad je Zelle
  const zellen = new Map();
  for (const platz of plaetze) {
    const schluessel = `${Math.floor(platz.lat / kante)}|${Math.floor(platz.lon / kante)}`;
    const zelle = zellen.get(schluessel) ?? { plaetze: [], anzahl: 0 };
    zelle.plaetze.push(platz);
    zelle.anzahl += platz.anzahl;
    zellen.set(schluessel, zelle);
  }
  return [...zellen.values()].map((zelle) => {
    const gewicht = zelle.plaetze.reduce((summe, p) => summe + p.anzahl, 0) || 1;
    const mittel = (feld) => zelle.plaetze
      .reduce((summe, p) => summe + p[feld] * p.anzahl, 0) / gewicht;
    const tiefen = zelle.plaetze.map((p) => p.schnittTiefe).filter((w) => typeof w === 'number');
    return {
      lat: mittel('lat'),
      lon: mittel('lon'),
      anzahl: zelle.anzahl,
      plaetze: zelle.plaetze,
      schnittTiefe: tiefen.length ? tiefen.reduce((a, b) => a + b, 0) / tiefen.length : null,
    };
  });
}
